import React, { useState, useEffect } from 'react';
import { Shield, ExternalLink, Check, Eye, Clock, AlertTriangle, XCircle, ChevronUp, ChevronDown } from 'lucide-react';
import api from '../../lib/api';
import { toast } from 'sonner';

const DETECTION_ICONS = {
  captcha: '🤖',
  block: '🚫',
  rate_limit: '⏱️',
  bot_detection: '🕵️',
};

const DETECTION_LABELS = {
  captcha: 'CAPTCHA',
  block: 'Blocked',
  rate_limit: 'Rate Limited',
  bot_detection: 'Bot Detection',
};

const SUBTYPE_LABELS = {
  soft_cloudflare: 'Cloudflare (Auto)',
  google_recaptcha: 'Google reCAPTCHA',
  hcaptcha: 'hCaptcha',
  cloudflare: 'Cloudflare Block',
  akamai: 'Akamai',
  imperva: 'Imperva',
  aws_waf: 'AWS WAF',
  sucuri: 'Sucuri',
  generic_captcha: 'Generic CAPTCHA',
  generic_block: 'Generic Block',
  '403': 'HTTP 403',
  '429': 'HTTP 429',
};

export default function Detections() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [activeType, setActiveType] = useState('all');
  const [stats, setStats] = useState({});
  const [sortBy, setSortBy] = useState('last_detected');
  const [sortOrder, setSortOrder] = useState('desc');

  useEffect(() => { loadLogs(); }, [page, activeType, sortBy, sortOrder]);

  const loadLogs = async () => {
    setLoading(true);
    try {
      const params = { page, limit: 50, sort_by: sortBy, sort_order: sortOrder };
      if (activeType !== 'all') params.detection_type = activeType;
      
      const res = await api.get('/api/admin/detection-logs', { params });
      setLogs(res.data.logs || []);
      setTotal(res.data.total);
      setHasMore(res.data.has_more);
      setStats(res.data.stats || {});
    } catch { toast.error('Failed to load detection logs'); }
    finally { setLoading(false); }
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
    setPage(1);
  };

  const getSortIcon = (field) => {
    if (sortBy !== field) return null;
    return sortOrder === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />;
  };

  const markAllReviewed = async (domain, detectionType) => {
    try {
      await api.post('/api/admin/detection-logs/review-all', null, {
        params: { domain, detection_type: detectionType }
      });
      setLogs(prev => prev.map(log => 
        log.domain === domain && log.detection_type === detectionType 
          ? { ...log, reviewed: 1 } : log
      ));
      // Update stats
      setStats(prev => ({
        ...prev,
        unreviewed: Math.max(0, (prev.unreviewed || 0) - 1)
      }));
      toast.success(`All ${domain} detections marked reviewed`);
    } catch { toast.error('Failed to update'); }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return ', ';
    return new Date(dateStr).toLocaleDateString('en-US', { 
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const types = [
    { id: 'all', label: 'All', icon: <Shield size={14} /> },
    { id: 'captcha', label: 'CAPTCHA', icon: <AlertTriangle size={14} /> },
    { id: 'block', label: 'Blocks', icon: <XCircle size={14} /> },
    { id: 'rate_limit', label: 'Rate Limits', icon: <Clock size={14} /> },
  ];

  const statCardStyle = {
    background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)',
    borderRadius: 'var(--radius-lg)', padding: 'var(--space-xl)', textAlign: 'center',
    boxShadow: 'var(--shadow-sm)',
  };

  return (
    <div className="page-content" style={{ maxWidth: '100%' }}>
      <div style={{ marginBottom: 'var(--space-xl)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ width: 44, height: 44, borderRadius: 'var(--radius-md)', background: 'var(--error-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>🛡️</span>
          <div>
            <h3 style={{ color: 'var(--text-primary)', fontSize: 18, fontWeight: 700, margin: 0 }}>Detections</h3>
            <p style={{ color: 'var(--text-secondary)', fontSize: 13, margin: '4px 0 0' }}>
              {total} unique · {stats.unreviewed || 0} unreviewed
            </p>
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 'var(--space-md)', marginBottom: 'var(--space-xl)' }}>
        <div style={statCardStyle}>
          <div style={{ fontSize: 20, marginBottom: 4 }}>🛡️</div>
          <div style={{ fontSize: 24, fontWeight: 800, color: 'var(--text-primary)', lineHeight: 1 }}>{stats.total || 0}</div>
          <div style={{ color: 'var(--text-secondary)', fontSize: 11, marginTop: 4 }}>Total</div>
          <div style={{ color: 'var(--success)', fontSize: 10, marginTop: 2 }}>
            {stats.total_resolved || 0} resolved
          </div>
        </div>
        {types.filter(t => t.id !== 'all').map(type => (
          <div key={type.id} onClick={() => setActiveType(activeType === type.id ? 'all' : type.id)} style={{
            ...statCardStyle,
            cursor: 'pointer',
            borderColor: activeType === type.id ? 'var(--accent-primary)' : 'var(--border-primary)',
          }}>
            <div style={{ fontSize: 20, marginBottom: 4 }}>{DETECTION_ICONS[type.id] || '🔍'}</div>
            <div style={{ fontSize: 24, fontWeight: 800, color: 'var(--error)', lineHeight: 1 }}>{stats[type.id] || 0}</div>
            <div style={{ color: 'var(--text-secondary)', fontSize: 11, marginTop: 4 }}>{type.label}</div>
            <div style={{ color: 'var(--success)', fontSize: 10, marginTop: 2 }}>
              {stats[`${type.id}_resolved`] || 0} resolved
            </div>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 6, marginBottom: 'var(--space-xl)', overflowX: 'auto' }}>
        {types.map(type => (
          <button key={type.id} onClick={() => { setActiveType(type.id); setPage(1); }} style={{
            display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 8, border: 'none',
            cursor: 'pointer', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
            background: activeType === type.id ? 'var(--accent-glow)' : 'var(--bg-tertiary)',
            color: activeType === type.id ? 'var(--accent-primary)' : 'var(--text-secondary)',
          }}>{type.icon} {type.label}</button>
        ))}
      </div>

      <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)', borderRadius: 'var(--radius-xl)', overflow: 'hidden', boxShadow: 'var(--shadow-sm)' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', minWidth: 900, borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--bg-tertiary)', borderBottom: '1px solid var(--border-primary)' }}>
                {[
                  { key: 'domain', label: 'Domain' },
                  { key: 'detection_type', label: 'Type' },
                  { key: 'sub_type', label: 'Details' },
                  { key: 'status_code', label: 'HTTP' },
                  { key: 'count', label: 'Count' },
                  { key: 'first_detected', label: 'First Seen' },
                  { key: 'last_detected', label: 'Last Seen' },
                  { key: 'reviewed', label: 'Status' },
                ].map(col => (
                  <th key={col.key} onClick={() => handleSort(col.key)} style={{ ...thStyle, cursor: 'pointer' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      {col.label} {getSortIcon(col.key)}
                    </span>
                  </th>
                ))}
                <th style={thStyle}>Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} style={{ textAlign: 'center', padding: 40 }}><div className="loader"></div></td></tr>
              ) : logs.length === 0 ? (
                <tr><td colSpan={9} style={{ textAlign: 'center', padding: 40, color: 'var(--text-secondary)' }}>
                  <div style={{ fontSize: 40, marginBottom: 8 }}>🎉</div>
                  <p style={{ fontSize: 14, fontWeight: 500 }}>No detections found</p>
                </td></tr>
              ) : (
                logs.map((log, i) => (
                  <tr key={`${log.domain}-${log.detection_type}-${i}`} style={{
                    borderBottom: '1px solid var(--border-primary)',
                    background: log.reviewed ? 'transparent' : 'var(--warning-bg)',
                    opacity: log.reviewed ? 0.7 : 1,
                  }}>
                    <td style={{ ...tdStyle, fontWeight: 600, color: 'var(--text-primary)' }}>
                      <a href={`https://${log.domain}`} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent-primary)', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 4 }}>
                        {log.domain} <ExternalLink size={10} />
                      </a>
                    </td>
                    <td style={tdStyle}>
                      <span style={{ padding: '3px 10px', borderRadius: 10, fontSize: 11, fontWeight: 600,
                        background: log.detection_type === 'captcha' ? 'var(--warning-bg)' : 'var(--error-bg)',
                        color: log.detection_type === 'captcha' ? 'var(--warning)' : 'var(--error)',
                      }}>
                        {DETECTION_ICONS[log.detection_type] || '🔍'} {DETECTION_LABELS[log.detection_type] || log.detection_type}
                      </span>
                    </td>
                    <td style={tdStyle}>
                      <span style={{ color: 'var(--text-secondary)', fontSize: 12 }}>
                        {SUBTYPE_LABELS[log.sub_type] || log.sub_type || ', '}
                      </span>
                    </td>
                    <td style={tdStyle}>
                      {log.status_code ? (
                        <span style={{ padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                          background: 'var(--error-bg)', color: 'var(--error)',
                        }}>{log.status_code}</span>
                      ) : <span style={{ color: 'var(--text-tertiary)' }}>, </span>}
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'center' }}>
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        minWidth: 28, height: 24, borderRadius: 12,
                        background: log.count > 5 ? 'var(--error-bg)' : 'var(--bg-tertiary)',
                        color: log.count > 5 ? 'var(--error)' : 'var(--text-secondary)',
                        fontSize: 12, fontWeight: 700,
                      }}>
                        {log.count}
                      </span>
                    </td>
                    <td style={tdStyle}><div style={{ color: 'var(--text-secondary)', fontSize: 12 }}>{formatDate(log.first_detected)}</div></td>
                    <td style={tdStyle}><div style={{ color: 'var(--text-secondary)', fontSize: 12 }}>{formatDate(log.last_detected)}</div></td>
                    <td style={tdStyle}>
                      {log.reviewed ? (
                        <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: 'var(--success)', fontSize: 12 }}><Check size={12} /> Reviewed</span>
                      ) : (
                        <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: 'var(--warning)', fontSize: 12 }}><Eye size={12} /> New</span>
                      )}
                    </td>
                    <td style={tdStyle}>
                      {!log.reviewed && (
                        <button onClick={() => markAllReviewed(log.domain, log.detection_type)} style={{
                          padding: '5px 12px', borderRadius: 6, border: '1px solid var(--success)',
                          background: 'transparent', color: 'var(--success)',
                          cursor: 'pointer', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap',
                        }}><Check size={11} /> Mark Reviewed</button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {hasMore && (
          <div style={{ textAlign: 'center', padding: 16 }}>
            <button onClick={() => setPage(p => p + 1)} disabled={loading} style={{
              padding: '10px 24px', borderRadius: 10, border: '1px solid var(--border-primary)',
              background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 13, fontWeight: 500,
            }}>{loading ? 'Loading...' : `Load More (${total - logs.length} remaining)`}</button>
          </div>
        )}
      </div>
    </div>
  );
}

const thStyle = { padding: '12px 14px', textAlign: 'left', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-secondary)', whiteSpace: 'nowrap' };
const tdStyle = { padding: '12px 14px', whiteSpace: 'nowrap' };